export const API_BASE_URL = process.env.REACT_APP_API_URL || '';

// Helper function to get auth headers with CORS
// Helper function to get auth headers with CORS
export const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Authorization': token ? `Bearer ${token}` : '',
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
    'Origin': window.location.origin
  };
};

// Update the upload URL function with proper CORS handling
export const getPresignedUrl = async (file, projectId) => {
  try {
    // First get the pre-signed URL
    const urlResponse = await fetch(`${API_BASE_URL}/api/upload-url`, {
      method: 'POST',
      mode: 'cors', // Explicitly set CORS mode
      credentials: 'same-origin', // Use same-origin for credentials
      headers: {
        ...getAuthHeaders(),
      },
      body: JSON.stringify({
        fileName: file.name,
        fileType: file.type,
        projectId
      })
    });

    if (!urlResponse.ok) {
      const errorData = await urlResponse.text();
      console.error('URL Response Error:', errorData);
      throw new Error(`Failed to get upload URL: ${urlResponse.status}`);
    }

    const { uploadUrl } = await urlResponse.json();

    // Then upload to S3
    const uploadResponse = await fetch(uploadUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': file.type,
      },
      body: file
    });

    if (!uploadResponse.ok) {
      throw new Error('Failed to upload file');
    }

    return true;
  } catch (error) {
    console.error('Error in getPresignedUrl:', error);
    throw error;
  }
};

export const handleResponse = async (response) => {
  const contentType = response.headers.get('content-type');
  const isJson = contentType && contentType.includes('application/json');
  
  try {
    const data = isJson ? await response.json() : await response.text();

    if (!response.ok) {
      // Handle 401 Unauthorized responses
      if (response.status === 401) {
        console.warn('Authentication failed - token may be expired or invalid');
        // Clear invalid tokens from localStorage
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        throw new Error('Token verification failed');
      }
      throw new Error(data.message || `Request failed with status ${response.status}`);
    }

    return data;
  } catch (error) {
    console.error('Response handling error:', error);
    throw error; // Preserve the original error
  }
};





// api.js - authService section
export const authService = {
  login: async (email, password) => {
    try {
      console.log("authService:login: ", `${API_BASE_URL}/api/auth/login`);
      const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ email, password })
      });
      
      const data = await handleResponse(response);
      
      return {
        token: data.access_token,
        user: {
          userId: data.user.user_id || data.user.id,
          email: data.user.email,
          firstName: data.user.first_name,
          lastName: data.user.last_name, 
          roles: data.user.roles  || [] // Ensure roles are passed through
        }
      };
    } catch (error) {
      console.error('Login service error:', error);
      throw error;
    }
  },

  verifyToken: async (token) => {
    try {
      if (!token) throw new Error('No token provided');

      const response = await fetch(`${API_BASE_URL}/api/auth/verify`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json' 
        }
      });
      
      return handleResponse(response);
    } catch (error) {
      console.error('Token verification error:', error);
      throw error;
    }
  }
};



export const projectService = {
  getProjects: async (token) => {
    const response = await fetch(`${API_BASE_URL}/api/projects`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to fetch projects');
    }
    return handleResponse(response);
  },

  createProject: async (projectName, token) => {
    const response = await fetch(`${API_BASE_URL}/api/projects`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        project_name: projectName
      })
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to create project');
    }
    return handleResponse(response);
  },

  deleteProject: async (projectId, token) => {
    const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to delete project');
    }
    return response.json();
  },

  uploadFile: async (projectId, file, token) => {
    try {
      // First get the pre-signed URL
      const urlResponse = await fetch(`${API_BASE_URL}/api/projects/${projectId}/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          fileName: file.name,
          fileType: file.type
          // Removed projectId from body since it's in the URL path
        })
      });

      if (!urlResponse.ok) {
        const error = await urlResponse.json();
        throw new Error(error.message || 'Failed to get upload URL');
      }

      const { uploadUrl } = await urlResponse.json();

      // Then upload the file directly to S3
      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': file.type
        },
        body: file
      });

      if (!uploadResponse.ok) {
        throw new Error('Failed to upload file');
      }

      return true;
    } catch (error) {
      console.error('Error in uploadFile:', error);
      throw error;
    }
  },

  getProjectDetails: async (projectId, token) => {
    try {
      if (!token) {
        throw new Error('No authentication token provided');
      }

      console.log('Making request with token:', token ? 'Present' : 'Missing');
      
      const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch project details');
      }

      return response.json();
    } catch (error) {
      console.error('Project service error:', error);
      throw error;
    }
  },

  uploadFile: async (projectId, file, token) => {
    // First get the pre-signed URL
    const urlResponse = await fetch(`${API_BASE_URL}/api/projects/${projectId}/upload`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        fileName: file.name,
        fileType: file.type
      })
    });

    if (!urlResponse.ok) {
      const error = await urlResponse.json();
      throw new Error(error.message || 'Failed to get upload URL');
    }

    const { uploadUrl } = await urlResponse.json();

    // Then upload the file directly to S3
    const uploadResponse = await fetch(uploadUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': file.type
      },
      body: file
    });

    if (!uploadResponse.ok) {
      throw new Error('Failed to upload file');
    }

    return true;
  },

  getProjectFileContent: async (projectId, fileId, token, subfolder = 'upload') => {
    const response = await fetch(
      `${API_BASE_URL}/api/projects/${projectId}/files/${fileId}/content?subfolder=${subfolder}`, 
      {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      }
    );
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to fetch file content');
    }
    return response.json();
  },


  deleteProjectFile: async (projectId, fileId, token, subfolder = 'upload') => {
    const response = await fetch(
      `${API_BASE_URL}/api/projects/${projectId}/files/${fileId}?subfolder=${subfolder}`, 
      {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      }
    );
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to delete file');
    }
    return response.json();
  },

  processProjectFile: async (projectId, fileId, token, subfolder = 'upload') => {
    const response = await fetch(
      `${API_BASE_URL}/api/projects/${projectId}/files/${fileId}/process?subfolder=${subfolder}`, 
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      }
    );
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to process file');
    }
    return response.json();
  }

};

export const reportService = {
  getReports: async (token) => {
    const response = await fetch(`${API_BASE_URL}/api/reports`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch reports');
    return response.json();
  }
};


export const newsletterService = {
  subscribe: async (data) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/newsletter/signup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(data)
      });
      
      return handleResponse(response);
    } catch (error) {
      console.error('Newsletter signup error:', error);
      throw error;
    }
  }
};

export const roleService = {
  // Get all users with their roles
  getUsers: async () => {
    console.log('getUsers: Starting request to /users');
    try {
      const response = await fetch(`${API_BASE_URL}/api/users`, {
        headers: getAuthHeaders()
      });
      console.log('getUsers: Raw response received:', response);
      
      const data = await handleResponse(response);
      console.log('getUsers: Parsed response data:', data);
      
      // Ensure we always return an array
      const users = data.users || [];
      console.log('getUsers: Processed users:', users);
      
      return users;
    } catch (error) {
      console.error('getUsers error:', error);
      throw error;
    }
  },

  // Get all roles
  getRoles: async () => {
    console.log('getRoles: Starting request to /roles');
    try {
      const response = await fetch(`${API_BASE_URL}/api/roles`, {
        headers: getAuthHeaders()
      });
      console.log('getRoles: Raw response received:', response);
      
      const data = await handleResponse(response);
      console.log('getRoles: Parsed response data:', data);
      
      // Ensure we always return an array
      const roles = data?.roles || [];
      console.log('getRoles: Processed roles:', roles);
      
      return roles;
    } catch (error) {
      console.error('getRoles error:', error);
      throw error;
    }
  },

  // Create a new role
  createRole: async (roleData) => {
    console.log('createRole: Creating role with data:', roleData);
    try {
      const response = await fetch(`${API_BASE_URL}/api/roles`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(roleData)
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Create role error:', error);
      throw error;
    }
  },

  // Assign role to user
  assignRole: async (userId, roleId) => {
    console.log(`assignRole: Assigning role ${roleId} to user ${userId}`);
    try {
        const response = await fetch(`${API_BASE_URL}/api/user/roles`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify({ user_id: userId, role_id: roleId })
        });
        return handleResponse(response);
    } catch (error) {
        console.error('Assign role error:', error);
        throw error;
    }
  },

  // Remove role from user
  removeRole: async (userId, roleId) => {
    console.log(`removeRole: Removing role ${roleId} from user ${userId}`);
    try {
      const response = await fetch(`${API_BASE_URL}/api/user/roles/${roleId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
        body: JSON.stringify({ user_id: userId, role_id: roleId })
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Remove role error:', error);
      throw error;
    }
  }
};

export const articleService = {
  // Get all articles
  getArticles: async (category = 'all') => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/articles${category !== 'all' ? `?category=${category}` : ''}`,
        {
          headers: getAuthHeaders()
        }
      );
      return handleResponse(response);
    } catch (error) {
      console.error('Get articles error:', error);
      throw error;
    }
  },

  // Create new article
  createArticle: async (articleData) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/articles`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(articleData)
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Create article error:', error);
      throw error;
    }
  },
};


// Subscription Services
export const subscriptionService = {
  createCheckoutSession: async (priceId, userId, userEmail, planId, billingInterval, metadata = {}) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/create-checkout-session`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          priceId,
          userId,
          userEmail,
          planId,
          billingInterval,
          metadata // Pass additional metadata
        })
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Create checkout session error:', error);
      throw error;
    }
  },

  // Subscribe to free tier (removing 'api' from path to match other endpoints)
  subscribeFreeTier: async (userId, planId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/subscriptions/free-tier`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          userId,
          planId
        })
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Free tier subscription error:', error);
      throw error;
    }
  },

  getCurrentSubscription: async (userId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/subscriptions/${userId}`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Get subscription error:', error);
      throw error;
    }
  },

  cancelSubscription: async (subscriptionId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/subscriptions/${subscriptionId}/cancel`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Cancel subscription error:', error);
      throw error;
    }
  },

  updateSubscription: async (subscriptionId, updateData) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/subscriptions/${subscriptionId}`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify(updateData)
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Update subscription error:', error);
      throw error;
    }
  },

  getSubscriptionPlans: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/subscription-plans`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Get subscription plans error:', error);
      throw error;
    }
  },

  getBillingHistory: async (userId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/subscriptions/${userId}/billing-history`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Get billing history error:', error);
      throw error;
    }
  },

  handleWebhookEvent: async (event) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/webhooks/stripe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Stripe-Signature': event.signature
        },
        body: JSON.stringify(event.data)
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Webhook handling error:', error);
      throw error;
    }
  },

  // Update the enrollInFreeCohort function
  enrollInCohort: async (cohort) => {
    try {
      // This would be an API call to your backend
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/user/cohort-enrollment`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          cohort_id: cohort.id,
          cohort_name: cohort.title,
          price: 0,
          payment_id: 'free_enrollment'
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to enroll in free cohort');
      }
      
      return true;
    } catch (error) {
      console.error('Error enrolling in free cohort:', error);
      throw error;
    }
  }
};

// Constants for subscription plans
export const SUBSCRIPTION_PLANS = {
  FREE: 'free_tier',
  BASIC: 'basic_tier',
  PREMIUM: 'premium_tier',
  ELITE: 'elite_tier'
};

// Constants for subscription status
export const SUBSCRIPTION_STATUS = {
  ACTIVE: 'active',
  CANCELED: 'canceled',
  PAST_DUE: 'past_due',
  UNPAID: 'unpaid',
  INCOMPLETE: 'incomplete',
  INCOMPLETE_EXPIRED: 'incomplete_expired',
  TRIALING: 'trialing'
};

export const portfolioService = {
  updateHoldings: async (holdings) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/user/portfolio-holdings`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ holdings })
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Update portfolio holdings error:', error);
      throw error;
    }
  },
  
  getHoldings: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/user`, {
        headers: getAuthHeaders()
      });
      const data = await handleResponse(response);
      
      // Add error handling and logging
      if (!data) {
        console.log('No data returned from API');
        return [];
      }

      const portfolio_holdings = data.portfolio_holdings || [];
      console.log("portfolio_holdings " + portfolio_holdings)

      // Check if portfolio_holdings exists directly on data
      if (portfolio_holdings.length!=0) {
        return portfolio_holdings;
      }
    
      // If no holdings found, return empty array
      console.log('No portfolio holdings found in response data:', data);
      
      return [];

    } catch (error) {
      console.error('Get portfolio holdings error:', error);
      throw error;
    }
  },

  updateAnalysis: async (analysis) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/user/portfolio-analysis`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ analysis })
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Update portfolio analysis error:', error);
      throw error;
    }
  },
  
  getAnalysis: async () => {
    try {
      console.log("fetch(`${API_BASE_URL}/user`")
      const response = await fetch(`${API_BASE_URL}/api/user`, {
        headers: getAuthHeaders()
      });
      const data = await handleResponse(response);
      console.log("data.user: " + data.user)
      const analysis = data.user.portfolio_analysis || [];
      console.log("analysis: " + analysis);
      return analysis;
    } catch (error) {
      console.error('Get portfolio analysis error:', error);
      throw error;
    }
  }
};



export const transactionsService = {
  getTransactionRoles: async (orgName = 'PHG RE') => {
    try {
      console.log('getTransactions org:', orgName);
      const response = await fetch(`${API_BASE_URL}/api/org/transaction-roles?org_name=${encodeURIComponent(orgName)}`, {
        headers: getAuthHeaders()
      });
      
      // Get raw response data
      const data = await handleResponse(response);
      console.log('Raw response data:', data);
      
      // Extract roles array from the response
      const roles = data?.roles || [];
      console.log('Extracted roles:', roles);
      
      return roles;
      
    } catch (error) {
      console.error('Get transaction roles error:', error);
      return [];
    }
  },
  
  updateTransactionRoles: async (roles, orgName = 'PHG RE') => {
    try {
      console.log('updateTransactionRoles org:', orgName, 'roles:', roles);
      
      // Format roles before sending
      const formattedRoles = roles.map(role => ({
        ...role,
        tiers: Array.isArray(role.tiers) ? role.tiers.map(tier => ({
          ...tier,
          lowerRange: Number(tier.lowerRange || 0),
          upperRange: Number(tier.upperRange || 0),
          percentage: Number(tier.percentage || 0),
          flatFee: Number(tier.flatFee || 0),
          allTransactions: Boolean(tier.allTransactions)
        })) : [],
        enabled: Boolean(role.enabled),
        divisions: Array.isArray(role.divisions) ? role.divisions : []
      }));

      const response = await fetch(`${API_BASE_URL}/api/org/transaction-roles`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          roles: formattedRoles,
          org_name: orgName
        })
      });

      const result = await handleResponse(response);
      
      // If we get here, the update was successful
      return { success: true, message: 'Roles updated successfully' };
      
    } catch (error) {
      console.error('Update transaction roles error:', error);
      throw new Error(error.message || 'Failed to update transaction roles');
    }
  },
  getChecklists: async (orgName = 'PHG RE') => {
    try {
      console.log('getChecklists org:', orgName);
      const response = await fetch(`${API_BASE_URL}/api/org/checklists?org_name=${encodeURIComponent(orgName)}`, {
        headers: getAuthHeaders()
      });
      
      // Get raw response data
      const data = await handleResponse(response);
      console.log('Raw checklist response data:', data);
      
      // Extract checklists array from the response
      const checklists = data?.checklists || [];
      console.log('Extracted checklists:', checklists);
      
      return data;
      
    } catch (error) {
      console.error('Get checklists error:', error);
      return { checklists: [] };
    }
  },
  
  updateChecklists: async (checklists, orgName = 'PHG RE') => {
    try {
      console.log('updateChecklists org:', orgName, 'checklists:', checklists);
      
      const response = await fetch(`${API_BASE_URL}/api/org/checklists`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          checklists,
          org_name: orgName
        })
      });

      const result = await handleResponse(response);
      
      // If we get here, the update was successful
      return { success: true, message: 'Checklists updated successfully' };
      
    } catch (error) {
      console.error('Update checklists error:', error);
      throw new Error(error.message || 'Failed to update checklists');
    }
  },

  // Transactions
  getTransactions: async (orgName = 'PHG RE') => {
    try {

      console.log('getTransactions org:', orgName)
      const response = await fetch(`${API_BASE_URL}/api/org/transactions?org_name=${encodeURIComponent(orgName)}`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Get transactions error:', error);
      throw error;
    }
  },
  
  
  updateTransactions: async (transactions, orgName = 'PHG RE') => {
    try {

      console.log('updateTransactions org:', orgName, 'transactions:', transactions)
      const response = await fetch(`${API_BASE_URL}/api/org/transactions`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          transactions,
          org_name: orgName
        })
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Update transactions error:', error);
      throw error;
    }
  }
};


// Add to api.js
export const realEstateService = {
  // Team management
  getRETeamMembers: async (orgName = 'PHG RE') => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/org/re_team_members?org_name=${encodeURIComponent(orgName)}`, {
        headers: getAuthHeaders()
      });
      const data = await handleResponse(response);
      
      // Handle the new structured response format
      if (data && data.re_team_members) {
        return data.re_team_members;
      }
      
      // Handle direct array response (backward compatibility)
      if (Array.isArray(data)) {
        return data;
      }
      
      // Default to empty array if no valid data format
      console.warn('Unexpected response format from RE team members endpoint:', data);
      return [];
    } catch (error) {
      console.error('Get team members error:', error);
      // Return empty array on error to avoid UI breakage
      return [];
    }
  },

  updateRETeamMembers: async (teamMembers, orgName = 'PHG RE') => {
    try {
      console.log('realEstateService:updateRETeamMemebrs: ', '\n orgName"', orgName , ' \n teamMembers:', teamMembers);
      const response = await fetch(`${API_BASE_URL}/api/org/re_team_members`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          re_team_members: teamMembers,
          org_name: orgName
        })
      });
      return handleResponse(response);
    } catch (error) {
      console.error('updateRETeamMembers', error);
      throw error;
    }
  },

  // Listings management
  getREListings: async (orgName = 'PHG RE') => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/org/re_listings?org_name=${encodeURIComponent(orgName)}`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Get listings error:', error);
      throw error;
    }
  },

  updateREListings: async (re_listings, orgName = 'PHG RE') => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/org/re_listings`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          re_listings,
          org_name: orgName
        })
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Update listings error:', error);
      throw error;
    }
  }
};
// Add to api.js

export const vendorService = {
  // Get all vendors for an organization
  getVendors: async (orgName = 'PHG RE') => {
    try {
      console.log('getVendors org:', orgName);
      const response = await fetch(`${API_BASE_URL}/api/org/vendors?org_name=${encodeURIComponent(orgName)}`, {
        headers: getAuthHeaders()
      });
      
      // Get raw response data
      const data = await handleResponse(response);
      console.log('Raw vendor data response:', data);
      
      // Extract vendors array from the response
      const vendors = data?.vendors || [];
      console.log('Extracted vendors:', vendors);
      
      return vendors;
      
    } catch (error) {
      console.error('Get vendors error:', error);
      return [];
    }
  },
  
  // Update vendors for an organization
  updateVendors: async (vendors, orgName = 'PHG RE') => {
    try {
      console.log('updateVendors org:', orgName, 'vendors:', vendors);
      
      const response = await fetch(`${API_BASE_URL}/api/org/vendors`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          vendors,
          org_name: orgName
        })
      });

      const result = await handleResponse(response);
      
      // If we get here, the update was successful
      return { success: true, message: 'Vendors updated successfully' };
      
    } catch (error) {
      console.error('Update vendors error:', error);
      throw new Error(error.message || 'Failed to update vendors');
    }
  }
};


export const teamFeedbackService = {
  // Get team feedback
  getTeamFeedback: async (orgName = 'PHG RE') => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/org/team-feedback?org_name=${encodeURIComponent(orgName)}`, {
        headers: getAuthHeaders()
      });
      
      // Get raw response data
      const data = await handleResponse(response);
      console.log('Raw team feedback response data:', data);
      
      // Extract feedback array from the response
      const teamFeedback = data?.team_feedback || [];
      console.log('Extracted team feedback:', teamFeedback);
      
      return teamFeedback;
      
    } catch (error) {
      console.error('Get team feedback error:', error);
      return [];
    }
  },
  
  // Update team feedback
  updateTeamFeedback: async (teamFeedback, orgName = 'PHG RE') => {
    try {
      console.log('updateTeamFeedback org:', orgName, 'feedback:', teamFeedback);
      
      const response = await fetch(`${API_BASE_URL}/api/org/team-feedback`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          team_feedback: teamFeedback,
          org_name: orgName
        })
      });

      const result = await handleResponse(response);
      
      // If we get here, the update was successful
      return { success: true, message: 'Team feedback updated successfully' };
      
    } catch (error) {
      console.error('Update team feedback error:', error);
      throw new Error(error.message || 'Failed to update team feedback');
    }
  }
};


// Add to api.js file
export const pipelineService = {
  getPipelineIntakes: async (orgName = 'PHG RE') => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/org/pipeline?org_name=${encodeURIComponent(orgName)}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });
      
      const data = await handleResponse(response);
      return data.pipeline || [];
    } catch (error) {
      console.error('Get pipeline intakes error:', error);
      return [];
    }
  },
  
  updatePipelineIntakes: async (intakes, orgName = 'PHG RE') => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/org/pipeline`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          pipeline: intakes,
          org_name: orgName
        })
      });
      
      return handleResponse(response);
    } catch (error) {
      console.error('Update pipeline intakes error:', error);
      throw error;
    }
  }
};

// Add to api.js file
export const cohortService = {
  getCohorts: async (orgName = 'PHG RE') => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/org/cohorts?org_name=${encodeURIComponent(orgName)}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });
      
      const data = await handleResponse(response);
      return data.cohorts || [];
    } catch (error) {
      console.error('Error fetching cohorts:', error);
      return [];
    }
  },
  
  updateCohorts: async (cohorts, orgName = 'PHG RE') => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/org/cohorts`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          cohorts: cohorts,
          org_name: orgName
        })
      });
      
      return handleResponse(response);
    } catch (error) {
      console.error('Error updating cohorts:', error);
      throw error;
    }
  },
  
  getTrainingModules: async (orgName = 'PHG RE') => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/org/training-modules?org_name=${encodeURIComponent(orgName)}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });
      
      const data = await handleResponse(response);
      return data.modules || [];
    } catch (error) {
      console.error('Error fetching training modules:', error);
      return [];
    }
  },
  
  updateTrainingModules: async (modules, orgName = 'PHG RE') => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/org/training-modules`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          modules: modules,
          org_name: orgName
        })
      });
      
      return handleResponse(response);
    } catch (error) {
      console.error('Error updating training modules:', error);
      throw error;
    }
  },
  getTrainingModules: async (orgName = 'PHG RE') => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/org/training-modules?org_name=${encodeURIComponent(orgName)}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });
      
      const data = await handleResponse(response);
      return data.modules || [];
    } catch (error) {
      console.error('Error fetching training modules:', error);
      return [];
    }
  },
  
  updateTrainingModules: async (modules, orgName = 'PHG RE') => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/org/training-modules`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          modules: modules,
          org_name: orgName
        })
      });
      
      return handleResponse(response);
    } catch (error) {
      console.error('Error updating training modules:', error);
      throw error;
    }
  }
};


export const orgService = {
  // Get all organizations
  getOrgs: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/orgs`, {
        headers: getAuthHeaders()
      });
      
      const data = await handleResponse(response);
      console.log('Retrieved organizations:', data);
      
      // Ensure we always return an array
      const orgs = data?.orgs || [];
      
      return orgs;
    } catch (error) {
      console.error('Get organizations error:', error);
      throw error;
    }
  },

  // Create a new organization
  createOrg: async (orgData) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/orgs`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(orgData)
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Create organization error:', error);
      throw error;
    }
  },

  // Get organization by ID
  getOrgById: async (orgId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/orgs/${orgId}`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Get organization by ID error:', error);
      throw error;
    }
  },

  // Update organization
  updateOrg: async (orgId, orgData) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/orgs/${orgId}`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify(orgData)
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Update organization error:', error);
      throw error;
    }
  },

  // Delete organization
  deleteOrg: async (orgId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/orgs/${orgId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Delete organization error:', error);
      throw error;
    }
  }
};


// Add to api.js file
export const checklistService = {
  getChecklists: async (orgName = 'PHG RE') => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/org/checklists?org_name=${encodeURIComponent(orgName)}`, {
        headers: getAuthHeaders()
      });
      
      // Get raw response data
      const data = await handleResponse(response);
      console.log('Raw checklist response data:', data);
      
      // Extract checklists array from the response
      const checklists = data?.checklists || [];
      console.log('Extracted checklists:', checklists);
      
      return checklists;
      
    } catch (error) {
      console.error('Get checklists error:', error);
      return [];
    }
  },
  
  updateChecklists: async (checklists, orgName = 'PHG RE') => {
    try {
      console.log('updateChecklists org:', orgName, 'checklists count:', checklists.length);
      
      const response = await fetch(`${API_BASE_URL}/api/org/checklists`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          checklists,
          org_name: orgName
        })
      });

      const result = await handleResponse(response);
      
      // If we get here, the update was successful
      return { success: true, message: 'Checklists updated successfully' };
      
    } catch (error) {
      console.error('Update checklists error:', error);
      throw new Error(error.message || 'Failed to update checklists');
    }
  },
  
  // Get a single checklist by ID
  getChecklistById: async (checklistId, orgName = 'PHG RE') => {
    try {
      const checklists = await this.getChecklists(orgName);
      return checklists.find(checklist => checklist.id === checklistId) || null;
    } catch (error) {
      console.error('Get checklist by ID error:', error);
      return null;
    }
  }
};

// Add to api.js file
export const proposalService = {
  generateProposalDraft: async (proposalData) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/proposals/generate`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(proposalData)
      });
      
      return handleResponse(response);
    } catch (error) {
      console.error('Error generating proposal:', error);
      throw error;
    }
  },
  
  saveProposal: async (proposalData) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/proposals/save`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(proposalData)
      });
      
      return handleResponse(response);
    } catch (error) {
      console.error('Error saving proposal:', error);
      throw error;
    }
  },
  
  getProposalTemplates: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/proposals/templates`, {
        headers: getAuthHeaders()
      });
      
      return handleResponse(response);
    } catch (error) {
      console.error('Error fetching proposal templates:', error);
      throw error;
    }
  },
  
  getProposalHistory: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/proposals/history`, {
        headers: getAuthHeaders()
      });
      
      return handleResponse(response);
    } catch (error) {
      console.error('Error fetching proposal history:', error);
      throw error;
    }
  }
};

// Add this to your existing api.js file as a new service

export const comparablesService = {
  generateComparables: async (propertyData) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/comparables/generate`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(propertyData)
      });
      
      return handleResponse(response);
    } catch (error) {
      console.error('Error generating comparables report:', error);
      throw error;
    }
  },
  
  saveReport: async (reportData) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/comparables/save`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(reportData)
      });
      
      return handleResponse(response);
    } catch (error) {
      console.error('Error saving comparables report:', error);
      throw error;
    }
  },
  
  getHistory: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/comparables/history`, {
        headers: getAuthHeaders()
      });
      
      return handleResponse(response);
    } catch (error) {
      console.error('Error fetching comparables history:', error);
      throw error;
    }
  }
};

export const milestoneService = {
  // Get user's milestone progress
  getUserProgress: async (category = null, packageType = null) => {
    try {
      let url = `${API_BASE_URL}/api/user/milestones`;
      const params = new URLSearchParams();
      
      if (category) params.append('category', category);
      if (packageType) params.append('package', packageType);
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
      
      const response = await fetch(url, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Get user progress error:', error);
      throw error;
    }
  },

  // Record user action for milestone tracking
  recordProgress: async (actionType, actionData = {}) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/user/milestone-progress`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          action_type: actionType,
          action_data: actionData
        })
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Record progress error:', error);
      throw error;
    }
  },

  // Get user achievements
  getUserAchievements: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/user/achievements`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Get achievements error:', error);
      throw error;
    }
  },

  // Get milestone leaderboard
  getLeaderboard: async (category = 'all', timeframe = 'all_time') => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/milestones/leaderboard?category=${category}&timeframe=${timeframe}`, 
        {
          headers: getAuthHeaders()
        }
      );
      return handleResponse(response);
    } catch (error) {
      console.error('Get leaderboard error:', error);
      throw error;
    }
  },

  // Admin: Get milestone analytics
  getAnalytics: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/milestone-analytics`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Get analytics error:', error);
      throw error;
    }
  },

  // Admin: Create new milestone
  createMilestone: async (milestoneData) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/milestones`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(milestoneData)
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Create milestone error:', error);
      throw error;
    }
  },

  // Admin: Get all milestones
  getAllMilestones: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/milestones`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Get all milestones error:', error);
      throw error;
    }
  },

  // Admin: Bulk process existing users
  bulkProcessUsers: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/milestones/bulk-process`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Bulk process error:', error);
      throw error;
    }
  }
};

// Milestone tracking helper functions
export const milestoneTracker = {
  // Track user login
  trackLogin: async () => {
    try {
      await milestoneService.recordProgress('login', {
        timestamp: new Date().toISOString()
      });
    } catch (error) {
      console.error('Error tracking login:', error);
    }
  },

  // Track profile completion
  trackProfileComplete: async (profileData) => {
    try {
      await milestoneService.recordProgress('profile_complete', {
        profile_data: profileData,
        completion_percentage: 100
      });
    } catch (error) {
      console.error('Error tracking profile completion:', error);
    }
  },

  // Track first calculation
  trackFirstCalculation: async (calculationType, result) => {
    try {
      await milestoneService.recordProgress('first_calculation', {
        calculation_type: calculationType,
        result: result,
        timestamp: new Date().toISOString()
      });
    } catch (error) {
      console.error('Error tracking first calculation:', error);
    }
  },

  // Track portfolio actions
  trackPortfolioAction: async (actionType, portfolioData) => {
    try {
      await milestoneService.recordProgress(`portfolio_${actionType}`, {
        action: actionType,
        portfolio_size: portfolioData?.length || 0,
        timestamp: new Date().toISOString()
      });
    } catch (error) {
      console.error('Error tracking portfolio action:', error);
    }
  },

  // Track project creation
  trackProjectCreation: async (projectData) => {
    try {
      await milestoneService.recordProgress('project_created', {
        project_name: projectData.name,
        project_type: projectData.type,
        timestamp: new Date().toISOString()
      });
    } catch (error) {
      console.error('Error tracking project creation:', error);
    }
  },

  // Track file upload
  trackFileUpload: async (fileName, fileType) => {
    try {
      await milestoneService.recordProgress('file_uploaded', {
        file_name: fileName,
        file_type: fileType,
        timestamp: new Date().toISOString()
      });
    } catch (error) {
      console.error('Error tracking file upload:', error);
    }
  },

  // Track cohort enrollment
  trackCohortEnrollment: async (cohortData) => {
    try {
      await milestoneService.recordProgress('cohort_enrolled', {
        cohort_id: cohortData.id,
        cohort_title: cohortData.title,
        price: cohortData.price,
        timestamp: new Date().toISOString()
      });
    } catch (error) {
      console.error('Error tracking cohort enrollment:', error);
    }
  },

  // Track report generation
  trackReportGeneration: async (reportType, reportData) => {
    try {
      await milestoneService.recordProgress('report_generated', {
        report_type: reportType,
        report_data: reportData,
        timestamp: new Date().toISOString()
      });
    } catch (error) {
      console.error('Error tracking report generation:', error);
    }
  },

  // Track feature usage
  trackFeatureUsage: async (featureName, usageData = {}) => {
    try {
      await milestoneService.recordProgress('feature_used', {
        feature_name: featureName,
        usage_data: usageData,
        timestamp: new Date().toISOString()
      });
    } catch (error) {
      console.error('Error tracking feature usage:', error);
    }
  },

  // Track sharing actions
  trackShare: async (shareType, sharedData) => {
    try {
      await milestoneService.recordProgress('content_shared', {
        share_type: shareType,
        shared_data: sharedData,
        timestamp: new Date().toISOString()
      });
    } catch (error) {
      console.error('Error tracking share:', error);
    }
  },

  // Track daily activity (for streak tracking)
  trackDailyActivity: async () => {
    try {
      await milestoneService.recordProgress('daily_activity', {
        date: new Date().toISOString().split('T')[0],
        timestamp: new Date().toISOString()
      });
    } catch (error) {
      console.error('Error tracking daily activity:', error);
    }
  }
};

// Default milestone definitions for system initialization
export const DEFAULT_MILESTONES = [
  // Onboarding Milestones
  {
    title: 'Welcome Aboard',
    description: 'Successfully created your account and logged in',
    category: 'onboarding',
    package_type: 'general',
    sequence_order: 1,
    difficulty: 'easy',
    points: 10,
    badge_icon: 'star',
    badge_color: 'blue',
    trigger_conditions: {
      action_type: 'login'
    },
    reward_type: 'badge'
  },
  {
    title: 'Profile Master',
    description: 'Completed your user profile with all required information',
    category: 'onboarding',
    package_type: 'general',
    sequence_order: 2,
    difficulty: 'easy',
    points: 15,
    badge_icon: 'star',
    badge_color: 'green',
    trigger_conditions: {
      action_type: 'profile_complete'
    },
    reward_type: 'badge'
  },

  // Financial Planning Milestones
  {
    title: 'Calculator Novice',
    description: 'Performed your first financial calculation',
    category: 'financial_planning',
    package_type: 'financial_planning',
    sequence_order: 1,
    difficulty: 'easy',
    points: 20,
    badge_icon: 'zap',
    badge_color: 'blue',
    trigger_conditions: {
      action_type: 'first_calculation'
    },
    reward_type: 'badge'
  },
  {
    title: 'Portfolio Pioneer',
    description: 'Added your first portfolio holdings',
    category: 'portfolio',
    package_type: 'portfolio_management',
    sequence_order: 1,
    difficulty: 'medium',
    points: 25,
    badge_icon: 'target',
    badge_color: 'purple',
    trigger_conditions: {
      action_type: 'portfolio_holdings_added'
    },
    reward_type: 'badge'
  },

  // Engagement Milestones
  {
    title: 'Project Creator',
    description: 'Created your first project',
    category: 'engagement',
    package_type: 'general',
    sequence_order: 1,
    difficulty: 'medium',
    points: 30,
    badge_icon: 'award',
    badge_color: 'green',
    trigger_conditions: {
      action_type: 'project_created'
    },
    reward_type: 'badge'
  },
  {
    title: 'File Uploader',
    description: 'Uploaded your first file to a project',
    category: 'engagement',
    package_type: 'general',
    sequence_order: 2,
    difficulty: 'easy',
    points: 15,
    badge_icon: 'zap',
    badge_color: 'blue',
    trigger_conditions: {
      action_type: 'file_uploaded'
    },
    reward_type: 'badge'
  },

  // Learning Milestones
  {
    title: 'Student',
    description: 'Enrolled in your first training cohort',
    category: 'learning',
    package_type: 'education',
    sequence_order: 1,
    difficulty: 'easy',
    points: 40,
    badge_icon: 'award',
    badge_color: 'purple',
    trigger_conditions: {
      action_type: 'cohort_enrolled'
    },
    reward_type: 'badge'
  },

  // Advanced Milestones
  {
    title: 'Report Generator',
    description: 'Generated your first analytical report',
    category: 'advanced',
    package_type: 'general',
    sequence_order: 1,
    difficulty: 'medium',
    points: 35,
    badge_icon: 'trophy',
    badge_color: 'gold',
    trigger_conditions: {
      action_type: 'report_generated'
    },
    reward_type: 'badge'
  },
  {
    title: 'Feature Explorer',
    description: 'Used 5 different platform features',
    category: 'advanced',
    package_type: 'general',
    sequence_order: 2,
    difficulty: 'hard',
    points: 50,
    badge_icon: 'trophy',
    badge_color: 'purple',
    trigger_conditions: {
      type: 'complex',
      milestone_id: 'feature_explorer'
    },
    reward_type: 'badge'
  },

  // Social Milestones
  {
    title: 'Sharer',
    description: 'Shared your first result or insight',
    category: 'social',
    package_type: 'general',
    sequence_order: 1,
    difficulty: 'medium',
    points: 25,
    badge_icon: 'star',
    badge_color: 'blue',
    trigger_conditions: {
      action_type: 'content_shared'
    },
    reward_type: 'badge'
  }
];

// Default achievement definitions
export const DEFAULT_ACHIEVEMENTS = [
  {
    title: 'Milestone Master',
    description: 'Completed 10 milestones',
    achievement_type: 'milestone',
    icon: 'trophy',
    color: 'gold',
    rarity: 'rare',
    unlock_criteria: {
      type: 'milestone_count',
      required_count: 10
    },
    points_value: 100
  },
  {
    title: 'Week Warrior',
    description: 'Completed milestones for 7 consecutive days',
    achievement_type: 'streak',
    icon: 'flame',
    color: 'orange',
    rarity: 'epic',
    unlock_criteria: {
      type: 'daily_streak',
      streak_days: 7
    },
    points_value: 150
  },
  {
    title: 'Portfolio Expert',
    description: 'Mastered all portfolio management features',
    achievement_type: 'mastery',
    icon: 'crown',
    color: 'purple',
    rarity: 'legendary',
    unlock_criteria: {
      type: 'category_mastery',
      category: 'portfolio'
    },
    points_value: 250
  }
];